type NullableString = string | null;
type NullableNumberOrString = number | string | null;

export interface RawMetalPriceOption {
    metal?: NullableString;
    karatPercent?: NullableNumberOrString;
    basePrice?: NullableNumberOrString;
    extendedPrice?: NullableNumberOrString;
}

export interface RawMetalKaratageOption {
    denumire?: NullableString;
    metalType?: NullableString;
    percent?: NullableNumberOrString;
}

export interface MetalPriceOption {
    metal: string;
    karatPercent: number;
    basePrice: number;
    extendedPrice: number;
}

export interface MetalKaratageOption {
    metal: string;
    karatPercent: number;
}

function toNumber(value: NullableNumberOrString | undefined): number {
    const numberValue = Number(value);
    return Number.isFinite(numberValue) ? numberValue : 0;
}

export function normalizeMetalPriceOptions(
    rawOptions: readonly RawMetalPriceOption[] = [],
): MetalPriceOption[] {
    return rawOptions
        .map(option => ({
            metal: (option.metal ?? '').trim().toUpperCase(),
            karatPercent: toNumber(option.karatPercent),
            basePrice: toNumber(option.basePrice),
            extendedPrice: toNumber(option.extendedPrice),
        }))
        .filter(option => option.metal && option.karatPercent > 0 && option.basePrice > 0)
        .sort(
            (leftOption, rightOption) =>
                leftOption.metal.localeCompare(rightOption.metal) ||
                leftOption.karatPercent - rightOption.karatPercent,
        );
}

export function normalizeMetalKaratageOptions(
    rawOptions: readonly RawMetalKaratageOption[] = [],
): MetalKaratageOption[] {
    const uniqueOptions = new Map<string, MetalKaratageOption>();

    rawOptions.forEach(option => {
        const metal = (option.denumire ?? option.metalType ?? '').trim().toUpperCase();
        const karatPercent = toNumber(option.percent);

        if (!metal || karatPercent <= 0) {
            return;
        }

        uniqueOptions.set(`${metal}_${karatPercent}`, { metal, karatPercent });
    });

    return Array.from(uniqueOptions.values()).sort(
        (leftOption, rightOption) =>
            leftOption.metal.localeCompare(rightOption.metal) ||
            leftOption.karatPercent - rightOption.karatPercent,
    );
}

export function getMetalTypeOptions(options: readonly MetalPriceOption[]): string[] {
    return Array.from(new Set(options.map(option => option.metal)));
}

export function getKaratOptionsForMetal(
    options: readonly MetalKaratageOption[],
    metal: string,
): number[] {
    const normalizedMetal = metal.trim().toUpperCase();
    return options
        .filter(option => option.metal === normalizedMetal)
        .map(option => option.karatPercent);
}

export function findMetalPriceOption(
    options: readonly MetalPriceOption[],
    metal: string,
    karatPercent: number,
): MetalPriceOption | undefined {
    const normalizedMetal = metal.trim().toUpperCase();
    return options.find(
        option => option.metal === normalizedMetal && option.karatPercent === karatPercent,
    );
}

export function findMetalPriceReferenceForMetal(
    options: readonly MetalPriceOption[],
    metal: string,
    selectedKaratPercent: number,
): MetalPriceOption | undefined {
    return (
        findMetalPriceOption(options, metal, selectedKaratPercent) ??
        options.find(option => option.metal === metal.trim().toUpperCase())
    );
}

export function calculateUnitPriceFromReperpret(
    option: MetalPriceOption | undefined,
    selectedKaratPercent: number,
    extendedDuration: boolean,
): number {
    if (!option || selectedKaratPercent <= 0) {
        return 0;
    }

    const basePrice =
        extendedDuration && option.extendedPrice > 0 ? option.extendedPrice : option.basePrice;
    return Math.round(((selectedKaratPercent * basePrice) / option.karatPercent) * 100) / 100;
}

export function buildCustomMetalName(metal: string, karatPercent: number): string {
    const normalizedMetal = metal.trim().toUpperCase();
    return normalizedMetal && karatPercent > 0 ? `${normalizedMetal}${karatPercent}` : '';
}
